import React from 'react';
import { useMutation } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';

import Modal from '../../components/layout/Modal';
import CampoUpload from '../../components/Formulario/CampoUpload';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import Badge from '../../components/layout/Badge';
import Descricao from '../../components/tipografia/Descricao';

type TransacaoPreview = {
  fitid?: string;
  data: string;
  valor: number;
  tipo: 'receita' | 'despesa';
  descricao: string;
  categoria?: string;
  centro_custo?: string;
  data_pagamento?: string;
  obs?: string;
  duplicado?: boolean;
};

const tipoOptions = [
  { id: 'receita', label: 'Receita' },
  { id: 'despesa', label: 'Despesa' },
];

export const ModalImportarLancamentos = ({
  show,
  onClose,
  onSuccess,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [transacoes, setTransacoes] = React.useState<TransacaoPreview[] | null>(
    null,
  );
  const [origem, setOrigem] = React.useState<'ofx' | 'csv' | null>(null);
  const [erroPreview, setErroPreview] = React.useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setTransacoes(null);
    setOrigem(null);
    setErroPreview(null);
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const previewMutator = useMutation(
    async () => {
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        '/api/lancamento_financeiro/importacao/preview',
        formData,
      );
      return response.data;
    },
    {
      onSuccess: (data: any) => {
        setErroPreview(null);
        setOrigem(data.origem);
        setTransacoes(data.transacoes);
      },
      onError: (err: any) => {
        const msg =
          err.response?.data?.msg ?? 'Erro ao processar o arquivo. Tente novamente.';
        setErroPreview(msg);
        toast(msg, {
          toastId: 'import-preview-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const confirmMutator = useMutation(
    async () => {
      const selecionadas = transacoes!.filter(t => !t.duplicado);
      return await axios.post(
        '/api/lancamento_financeiro/importacao/confirmar',
        {
          origem,
          filename: file?.name,
          transacoes: selecionadas,
        },
      );
    },
    {
      onSuccess: (res: any) => {
        const { inseridos, pulados } = res.data;
        toast(`${inseridos} lançamento(s) importado(s), ${pulados} pulado(s)`, {
          toastId: 'import-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        onSuccess();
        handleClose();
      },
      onError: () => {
        toast('Ocorreu um erro ao importar os lançamentos.', {
          toastId: 'import-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const handleTipoChange = (t: TransacaoPreview, tipo: string) => {
    setTransacoes(prev => prev!.map(x => (x === t ? { ...x, tipo: tipo as any } : x)));
  };

  const totalSelecionadas =
    transacoes?.filter(t => !t.duplicado).reduce((s, t) => s + t.valor, 0) ?? 0;

  const headers: DataTableHeader[] = [
    {
      label: 'Data',
      width: '110px',
      render: (row: any) =>
        row.data ? format(parseISO(row.data), 'dd/MM/yyyy') : '-',
    },
    { label: 'Descrição', column: 'descricao' },
    {
      label: 'Tipo',
      width: '140px',
      render: (row: any) => (
        <CampoSeletor
          noSearch
          instanceId={`tipo-${transacoes?.indexOf(row)}`}
          value={row.tipo}
          onChange={(v: string) => handleTipoChange(row, v)}
          options={tipoOptions}
          disabled={row.duplicado}
        />
      ),
    },
    {
      label: 'Valor',
      width: '130px',
      alignRight: true,
      render: (row: any) =>
        (row.tipo === 'despesa' ? '- ' : '') +
        Number(row.valor ?? 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }),
    },
    {
      label: '',
      width: '110px',
      render: (row: any) =>
        row.duplicado ? <Badge variant="secondary">Duplicado</Badge> : null,
    },
  ];

  return (
    <Modal
      instanceId="modal-importar-lancamentos"
      show={show}
      size="lg"
      title="Importar lançamentos"
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={() =>
        transacoes
          ? confirmMutator.mutate()
          : previewMutator.mutate()
      }
      confirmProps={{
        label: transacoes ? 'Importar' : 'Analisar arquivo',
        disabled:
          !transacoes && (!file || previewMutator.isLoading) ||
          (transacoes && confirmMutator.isLoading),
      }}
      cancelProps={{ label: 'Cancelar' }}
    >
      {!transacoes ? (
        <div>
          <Descricao>
            Selecione um arquivo <b>.ofx</b>/<b>.ofc</b> (extrato bancário) ou{' '}
            <b>.csv</b>.
          </Descricao>
          <CampoUpload
            accept=".ofx,.qfx,.ofc,.csv,.txt"
            maxFiles={1}
            value={file ? [file] : []}
            onChange={(files: File[]) => setFile(files[0] ?? null)}
            disabled={previewMutator.isLoading}
          />
          <Descricao style={{ marginTop: 12 }}>
            Para CSV, a primeira linha deve conter o cabeçalho. Colunas
            reconhecidas: <b>data</b>, <b>descricao</b> e <b>valor</b>{' '}
            (obrigatórias); <b>tipo</b>, <b>categoria</b>, <b>centro_custo</b>,{' '}
            <b>data_pagamento</b> e <b>obs</b> (opcionais).
          </Descricao>
          {erroPreview && (
            <Descricao style={{ color: 'var(--tc-color-danger-600)', marginTop: 8 }}>
              {erroPreview}
            </Descricao>
          )}
        </div>
      ) : (
        <div>
          <Descricao>
            {transacoes.filter(t => !t.duplicado).length} transação(ões) para
            importar ·{' '}
            {transacoes.filter(t => t.duplicado).length} duplicada(s) ·
            Total:{' '}
            {totalSelecionadas.toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </Descricao>
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <DataTable
              cabecalhos={headers}
              linhas={transacoes}
              highlightLine={(row: any) => !!row.duplicado}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};